import React, { useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { indicatorBucket, bucketToColor } from '../../utils/indicatorBuckets';
import { excludeHse } from '../../Data/missionSubkpiFilter';
import styles from './PerformanceSummary.module.css';

/**
 * MissionStatusCard — compact mission sub-KPI status overview
 * for the Summary tab's 2-up row (alongside MilestoneList).
 *
 * Categorises sub-KPIs into completed / active / look-ahead and
 * renders each with a colour-coded dot and progress text.
 *
 * Supports an All / Reporting Period toggle.
 */
type Props = {
  selectedItem: any;
};

const MissionStatusCard: React.FC<Props> = ({ selectedItem }) => {
  const { t } = useAppTranslation('kpi');
  const [mode, setMode] = useState<'all' | 'period'>('all');

  const today = new Date().toISOString().slice(0, 10);
  const cadence = String(
    (selectedItem as any)?.project?.reporting_cadence || 'monthly',
  );

  // Gather mission sub-KPIs from selectedItem.
  // DEV-822 spillover: exclude HSE — it's a separate always-on safety
  // surface, not a Mission output. Keeping HSE here would clutter the
  // status card with always-zero "incident" rows.
  const subkpis: any[] = (() => {
    const raw = (selectedItem as any)?.mission_subkpis;
    let arr: any[];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && typeof raw === 'object') arr = Object.values(raw);
    else arr = [];
    return excludeHse(arr);
  })();

  if (subkpis.length === 0) {
    return (
      <section className={styles.card} aria-labelledby="mission-status-title">
        <h3 id="mission-status-title" className={styles.cardTitle}>
          {t('summary.missionStatus') || 'Mission Status'}
        </h3>
        <div className={styles.empty}>
          {t('summary.noMissionKpis') || 'No mission outcomes configured for this project.'}
        </div>
      </section>
    );
  }

  // Categorise
  const completed: any[] = [];
  const active: any[] = [];
  const lookAhead: any[] = [];

  for (const sk of subkpis) {
    const vt = String(sk?.value_type ?? 'numeric');
    const val = sk?.latest_value;
    const goal = sk?.target_goal;
    const dl = sk?.deadline_date;
    const startDate = sk?.tracking_start_date;

    const goalMet =
      vt === 'binary'
        ? val != null && val >= 1
        : val != null && goal != null && goal > 0 && val >= goal;

    const notStarted = startDate && startDate > today;

    if (notStarted) {
      lookAhead.push(sk);
    } else if (goalMet && dl) {
      completed.push(sk);
    } else {
      active.push(sk);
    }
  }

  const statusText = (sk: any, variant: 'completed' | 'active' | 'upcoming') => {
    const vt = String(sk?.value_type ?? 'numeric');
    const val = sk?.latest_value;
    const goal = sk?.target_goal;
    const unit = sk?.uom ?? '';
    const startDate = sk?.tracking_start_date;

    if (variant === 'completed') {
      return vt === 'binary'
        ? t('mission.achieved') || 'Achieved'
        : `${val ?? '—'} of ${goal ?? '?'} ${unit}`.trim();
    }
    if (variant === 'active') {
      if (vt === 'binary') return t('mission.awaiting') || 'Awaiting';
      if (val != null && goal != null) return `${val} of ${goal} ${unit}`.trim();
      return t('mission.noDataYet') || 'No data yet';
    }
    const fmt = startDate
      ? new Date(startDate + 'T00:00:00').toLocaleDateString(undefined, {
          month: 'short', day: 'numeric', year: 'numeric',
        })
      : '';
    return t('mission.startsOn', { date: fmt }) || `Starts ${fmt}`;
  };

  const renderRow = (sk: any, variant: 'completed' | 'active' | 'upcoming') => {
    const name = sk?.label || String(sk?.subKpiKey ?? '').replace(/[-_]/g, ' ');
    const colorCode = sk?.indicator_color_value;
    const color =
      variant === 'upcoming'
        ? 'var(--text-quaternary)'
        : bucketToColor(indicatorBucket(Number(colorCode ?? 0)));
    const dl = sk?.deadline_date;

    let overdue: React.ReactNode = null;
    if (variant === 'active' && dl) {
      const daysLeft = Math.ceil((new Date(dl).getTime() - Date.now()) / 86400000);
      if (daysLeft < 0) {
        overdue = (
          <span
            className={styles.milestoneOverdue}
            style={{ color: 'var(--color-error)' }}
          >
            {t('mission.overdue') || 'Overdue'}
          </span>
        );
      } else if (daysLeft <= 30) {
        const c = daysLeft <= 7 ? 'var(--color-error)' : 'var(--color-warning)';
        overdue = (
          <span className={styles.milestoneOverdue} style={{ color: c }}>
            {daysLeft}d
          </span>
        );
      }
    }

    return (
      <div key={sk?.subKpiKey} className={styles.milestoneRow} style={{ opacity: variant === 'upcoming' ? 0.7 : 1 }}>
        <span className={styles.milestoneDot} style={{ background: color }} />
        <span className={styles.milestoneName}>{name}</span>
        <span className={styles.milestoneDate}>{statusText(sk, variant)}</span>
        {overdue}
      </div>
    );
  };

  const sectionLabel = (label: string, count: number) => (
    <div style={{
      fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)',
      textTransform: 'uppercase', letterSpacing: '0.04em',
      marginTop: 8, marginBottom: 4,
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {label}
      <span style={{
        fontSize: 10, fontWeight: 700, background: 'var(--bg-hover)',
        borderRadius: 8, padding: '1px 6px', color: 'var(--text-secondary)',
      }}>{count}</span>
    </div>
  );

  return (
    <section className={styles.card} aria-labelledby="mission-status-title">
      <div className={styles.cardHeader}>
        <h3 id="mission-status-title" className={styles.cardTitle}>
          {t('summary.missionStatus') || 'Mission Status'}
        </h3>
        <div style={{ display: 'flex', gap: 2, background: 'var(--bg-hover)', borderRadius: 6, padding: 2 }}>
          <button
            type="button"
            onClick={() => setMode('all')}
            style={{
              fontSize: 10, fontWeight: mode === 'all' ? 600 : 400,
              padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: mode === 'all' ? 'var(--bg-surface)' : 'transparent',
              color: mode === 'all' ? 'var(--text-primary)' : 'var(--text-tertiary)',
            }}
          >{t('summary.modeAll') || 'All'}</button>
          <button
            type="button"
            onClick={() => setMode('period')}
            style={{
              fontSize: 10, fontWeight: mode === 'period' ? 600 : 400,
              padding: '2px 8px', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: mode === 'period' ? 'var(--bg-surface)' : 'transparent',
              color: mode === 'period' ? 'var(--text-primary)' : 'var(--text-tertiary)',
            }}
          >{t('summary.modePeriod') || 'Period'}</button>
        </div>
      </div>

      {mode === 'period' && (
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6, fontStyle: 'italic' }}>
          {t('summary.periodNote', { cadence }) || `Showing items within the current ${cadence} reporting period.`}
        </div>
      )}

      <div className={styles.milestoneRows}>
        {active.length > 0 && (
          <>
            {sectionLabel(t('summary.active') || 'Active', active.length)}
            {active.map((sk) => renderRow(sk, 'active'))}
          </>
        )}
        {completed.length > 0 && (
          <>
            {sectionLabel(t('summary.completed') || 'Completed', completed.length)}
            {completed.map((sk) => renderRow(sk, 'completed'))}
          </>
        )}
        {lookAhead.length > 0 && (
          <>
            {sectionLabel(t('summary.lookAhead') || 'Look-Ahead', lookAhead.length)}
            {lookAhead.map((sk) => renderRow(sk, 'upcoming'))}
          </>
        )}
      </div>
    </section>
  );
};

export default MissionStatusCard;
